import React, { useState, useEffect, useCallback, useRef } from "react";
import "./Profile.scss";
import { Link, useParams } from "react-router-dom";
import { Stack } from "@mui/system";
import InstagramIcon from "@mui/icons-material/Instagram";
import { FollowButton } from "./FollowButton";
import LoadingView from "../components/elements/LoadingView";
import { getUserById } from "../services/user";
import ImportExportIcon from "@mui/icons-material/ImportExport";
import { getTrip, getUserTrips, isUserALocalPartnerOrLeader } from "../services/trip";
import ExpandCircleDownOutlinedIcon from "@mui/icons-material/ExpandCircleDownOutlined";
import ReactPlayer from "react-player";
import { getUserReviews, getUserExtraMedia } from "../services/user";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import { capitalize, useTheme } from "@mui/material";
import useQueryParams from "../customHooks/useQueryParams";
import dayjs from "dayjs";
import { ImageResizable } from "../components/elements/ImageResizeable";
import { useGlobalState } from "../GlobalStates";

import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined";
import FlutterDashIcon from "@mui/icons-material/FlutterDash";
import { useTabletOrMobile, useIsMobile } from "../hooks/responsive";
import { useTranslation } from "react-i18next";
import Dialog from "@mui/material/Dialog";
import CloseIcon from "@mui/icons-material/Close";
import { useUserLocale } from "../customHooks/useUserLocale";
import { useMyClubs } from "../services/club";

import { getDDMMMDate } from "../utils/utils";
import { Flag, MessageCircle, MoreHorizontal } from "lucide-react";
import { DownloadAppModal } from "../components/DownloadAppModal";
import { ProfileActionSheetModal } from "./ProfileActionSheetModal";
import { ReportUserDialog } from "./Connections/components/ReportUserDialog";
import { openNativeProfile } from "../utils/nativeProfileBridge";
import { openOwnProfileBooking } from "./profileBookingNavigation";
import { hasPlansToManage } from "../utils/clubPlans";
import { tripLinkClick } from "../utils/astroTrips";

// Entry point to planning: invite a first-time host to start something, then
// switch to management copy once they have created a club or any event.
const ClubProfileButton = () => {
  const { t } = useTranslation();
  const { data } = useMyClubs(true);
  const hasPlans = hasPlansToManage(data?.clubs);
  return (
    <Link to="/club/manage/events">
      <button
        className="ProfileEditButton"
        style={{
          backgroundColor: "white",
          color: "deepskyblue",
          border: "1px solid deepskyblue",
        }}
      >
        {hasPlans
          ? t("club_manage_plans", { defaultValue: "Manage plans" })
          : t("club_plan_something_fun", { defaultValue: "Plan something fun" })}
      </button>
    </Link>
  );
};

export const UserProfile = ({ passing_user_id = null, hideFixedActionButton = false }) => {
  const { language, community, loading, handleChangeLocale } = useUserLocale();
  const { t } = useTranslation();

  const { palette } = useTheme();
  const [_, setOpenAlertBannerMsg] = useGlobalState("openAlertBannerMsg");

  const [openDuckLevelsDialog, setOpenDuckLevelsDialog] = useState(false);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [showDownloadAppModal, setShowDownloadAppModal] = useState(false);
  const [showActionSheet, setShowActionSheet] = useState(false);

  const { user_id } = useParams();
  const navigate = useNavigate();
  const trip_id = useQueryParams().get("trip_id");
  const [user] = useGlobalState("user");

  const [profile, setProfile] = useState(null);
  const [userPastTrips, setUserPastTrips] = useState([]);
  const [userUpcomingTrips, setUserUpcomingTrips] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [extraMedia, setExtraMedia] = useState([]);
  const [
    { is_trip_leader, is_local_partner, local_partner_country },
    setIsUserALocalPartnerOrLeader,
  ] = useState({ is_trip_leader: false, is_local_partner: false });

  const isMobile = useTabletOrMobile();
  const isMobileOnly = useIsMobile();

  const userId = passing_user_id ? passing_user_id : user_id;

  const isUserProfile = userId === user?.id;

  const isInApp = window.isInApp;

  const openInstagramProfile = (instagramUrl) => {
    // Extract username from Instagram URL
    const urlParts = instagramUrl.split("/");
    const username = urlParts[urlParts.length - 1] || urlParts[urlParts.length - 2];
    const appLink = `instagram://user?username=${username}`;
    const webLink = `https://instagram.com/${username}`;

    // Try opening the Instagram app first
    window.location.href = appLink;

    // Fallback to web version after a delay if the app is not installed
    setTimeout(() => {
      window.location.href = webLink;
    }, 500); // Adjust the delay as needed
  };

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const profileRes = await getUserById(userId);
        setProfile(profileRes.data);
      } catch (error) {
        console.error(error);
      }
    };

    const getIsUserALocalPartnerOrLeader = async () => {
      const { is_trip_leader, is_local_partner, local_partner_country } = (
        await isUserALocalPartnerOrLeader(userId, trip_id)
      ).data;
      setIsUserALocalPartnerOrLeader({
        is_trip_leader,
        is_local_partner,
        local_partner_country,
      });
    };

    const getUserPastSotravelTrips = async () => {
      const res = await getUserTrips(userId);
      setUserUpcomingTrips(res.data.upcomingTrips);
      setUserPastTrips(res.data.pastTrips);
    };

    const getReviews = async () => {
      try {
        const res = await getUserReviews(userId);
        const mediaRes = await getUserExtraMedia(userId);
        setReviews(
          isUserProfile
            ? res.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
            : res.data
        );
        setExtraMedia(mediaRes.data);
      } catch (err) {
        console.log("failed to get user reviews", err);
      }
    };

    fetchProfile();
    getUserPastSotravelTrips();
    getReviews();

    if (trip_id) {
      getIsUserALocalPartnerOrLeader();
    }
  }, [trip_id, userId]);

  const DuckLevelsDialog = () => {
    return (
      <Dialog
        open={openDuckLevelsDialog}
        onClose={() => setOpenDuckLevelsDialog(false)}
        aria-labelledby="duck-levels-dialog-title"
        fullWidth={isMobile}
        maxWidth="sm"
      >
        <div style={{ padding: "1.5rem" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "1rem",
            }}
          >
            <h2
              id="duck-levels-dialog-title"
              style={{ margin: 0, fontWeight: "bold", fontSize: "1.5rem" }}
            >
              {t("duck_levels")}
            </h2>
            <button
              onClick={() => setOpenDuckLevelsDialog(false)}
              style={{ background: "none", border: "none", cursor: "pointer" }}
            >
              <CloseIcon />
            </button>
          </div>

          <Stack spacing={2}>
            <div
              className="duck-level-item"
              style={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <div style={{ flex: 1 }}>
                <strong>{t("duckling")}</strong>
                <p>{t("community_member")}</p>
              </div>
              <FlutterDashIcon style={{ color: "#9E9E9E", fontSize: "28px" }} />
            </div>

            <div
              className="duck-level-item"
              style={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <div style={{ flex: 1 }}>
                <strong>{t("junior_duck")}</strong>
                <p>
                  {t("we_all_start_somewhere")}
                </p>
              </div>
              <FlutterDashIcon style={{ color: "#4FC3F7", fontSize: "28px" }} />
            </div>

            <div
              className="duck-level-item"
              style={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <div style={{ flex: 1 }}>
                <strong>{t("senior_duck")}</strong>
                <p>
                  {t("experienced_group_leader")}
                </p>
              </div>
              <FlutterDashIcon style={{ color: "#2196F3", fontSize: "28px" }} />
            </div>

            <div
              className="duck-level-item"
              style={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <div style={{ flex: 1 }}>
                <strong>{t("super_duck")}</strong>
                <p>
                  {t("group_leader_high_aura")}
                </p>
              </div>
              <FlutterDashIcon style={{ color: "#FFC107", fontSize: "28px" }} />
            </div>
          </Stack>
        </div>
      </Dialog>
    );
  };

  const scrollValue = 298;
  const carouselReviewRef = useRef(null);
  const scrollReviewLeft = () => {
    carouselReviewRef.current.scrollLeft -= scrollValue;
  };
  const scrollReviewRight = () => {
    carouselReviewRef.current.scrollLeft += scrollValue;
  };
  const carouselMediaRef = useRef(null);
  const scrollMediaLeft = () => {
    carouselMediaRef.current.scrollLeft -= scrollValue;
  };
  const scrollMediaRight = () => {
    carouselMediaRef.current.scrollLeft += scrollValue;
  };

  const displayExtraMedia = (url, type) => {
    if (type === "image") {
      return (
        <img
          src={url}
          style={{
            height: "100%",
            width: "100%",
            objectFit: "cover",
            borderRadius: "10px",
          }}
        />
      );
    } else if (type === "video") {
      return <ReactPlayer url={url} controls playing={false} width="100%" height="100%" />;
    }
  };
  const [reviewMoreIndex, setReviewMoreIndex] = useState(-1);
  const reviewMaxLength = 120;
  const shortenText = (text) => {
    if (text.length === 0) {
      const positiveWords = [
        t("awesome"),
        t("great"),
        t("amazing"),
        t("fantastic"),
      ];
      return positiveWords[Math.floor(Math.random() * positiveWords.length)];
    }
    if (text.length > reviewMaxLength) {
      return `"${text.slice(0, reviewMaxLength)} ...`;
    }
    return `"${text}"`;
  };

  const openReviewerProfile = (reviewerUserId) => {
    if (!reviewerUserId || openNativeProfile(reviewerUserId)) return;
    navigate(`/profile/${reviewerUserId}`);
  };

  const handleAppNavigation = () => {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage("connectionNavigationTo");
    } else {
      console.error("ReactNativeWebView bridge is not available");
    }
  };

  if (!profile) {
    return <LoadingView />;
  }

  return (
    <div className="ProfileWrapper">
      {/* Duck Status Button - Only show for Jr Duck and above */}
      {isUserProfile && profile.host_type && profile.host_type !== "Duckling" && (
        <Link to="/profile/duck" style={{ textDecoration: "none" }}>
          <button
            className="app-floating-card"
            style={{
              padding: "15px 24px",
              backgroundColor: "black",
              border: "none",
              borderRadius: "25px",
              color: "white",
              fontSize: "15px",
              fontWeight: "700",
              cursor: "pointer",
              margin: "auto",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              position: "fixed",
              // Above the app's floating tab bar when present.
              bottom: "calc(1rem + var(--app-tabbar-inset, 0px))",
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 10000,
              width: "12.7rem",
              height: "3rem",
              boxShadow: "0px 2px 2px rgba(0, 0, 0, 0.25)",
            }}
          >
            <ImportExportIcon />
            {t("switch_to_leading")}
          </button>
        </Link>
      )}

      <div className="ProfileContainer">
        {/* 3-dot menu button in top right for mobile only (only for other users' profiles) */}
        {!isUserProfile && isMobileOnly && !hideFixedActionButton && (
          <div className="tw-fixed tw-right-4 tw-top-4 tw-z-[1000]">
            <button
              onClick={() => setShowActionSheet(true)}
              className="tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-full tw-text-gray-700 tw-transition-colors hover:tw-bg-gray-100"
              title="More options"
            >
              <MoreHorizontal className="tw-h-6 tw-w-6" />
            </button>
          </div>
        )}
        <div className="ProfileImageAndDescContainer">
          <div className="ProfileImageContainer">
            <img src={profile.image} alt={profile.display_name} />
            {(is_trip_leader || is_local_partner) && (
              <div
                style={{
                  display: "flex",
                  position: "absolute",
                  bottom: "0.1rem",
                  right: "0.1rem",
                  height: "2.5rem",
                  width: "2.5rem",
                  borderRadius: "50%",
                  backgroundColor: palette.primary.main,
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {is_local_partner ? (
                  <Icon icon="ic:round-verified-user" color="white" fontSize={"1.8rem"} />
                ) : (
                  <Icon icon="mdi:crown" color="white" fontSize={"1.8rem"} />
                )}
              </div>
            )}
          </div>

          <Stack className="ProfileSocialsContainer">
            {isInApp ? (
              isUserProfile ? (
                <button
                  className="ProfileFollowContainer"
                  onClick={() => {
                    handleAppNavigation();
                  }}
                >
                  <span className="ProfileFollowNumber">{user.following_count}</span>
                  <span className="ProfileFollowText">
                    {t("following")}
                  </span>
                </button>
              ) : (
                <button
                  className="ProfileFollowContainer"
                  onClick={() => {
                    handleAppNavigation();
                  }}
                >
                  <span className="ProfileFollowNumber">{profile.following_count}</span>
                  <span className="ProfileFollowText">
                    {t("following")}
                  </span>
                </button>
              )
            ) : (
              <Link to={isUserProfile ? `/profile/connections` : `/profile/${user_id}/connections`}>
                <button className="ProfileFollowContainer">
                  <span className="ProfileFollowNumber">
                    {isUserProfile ? user.following_count : profile.following_count}
                  </span>
                  <span className="ProfileFollowText">
                    {t("following")}
                  </span>
                </button>
              </Link>
            )}

            {isInApp ? (
              isUserProfile ? (
                <button
                  className="ProfileFollowContainer"
                  onClick={() => {
                    handleAppNavigation();
                  }}
                >
                  <span className="ProfileFollowNumber">{user.follower_count}</span>
                  <span className="ProfileFollowText">
                    {t("followers")}
                  </span>
                </button>
              ) : (
                <button
                  className="ProfileFollowContainer"
                  onClick={() => {
                    handleAppNavigation();
                  }}
                >
                  <span className="ProfileFollowNumber">{profile.follower_count}</span>
                  <span className="ProfileFollowText">
                    {t("followers")}
                  </span>
                </button>
              )
            ) : (
              <Link to={isUserProfile ? `/profile/connections` : `/profile/${user_id}/connections`}>
                <button className="ProfileFollowContainer">
                  <span className="ProfileFollowNumber">
                    {isUserProfile ? user.follower_count : profile.follower_count}
                  </span>
                  <span className="ProfileFollowText">
                    {t("followers")}
                  </span>
                </button>
              </Link>
            )}

            <button className="ProfileFollowContainer">
              <span className="ProfileFollowNumber">{profile.credits}</span>
              <span className="ProfileFollowText">
                {t("socredits")}
              </span>
            </button>
          </Stack>

          {isUserProfile ? (
            <Stack className="ProfileSocialsContainer">
              {isInApp ? (
                <button
                  className="ProfileEditButton"
                  onClick={() => {
                    if (window.ReactNativeWebView) {
                      window.ReactNativeWebView.postMessage("navigateToEditProfile");
                    } else {
                      console.error("ReactNativeWebView bridge is not available");
                    }
                  }}
                >
                  {t("edit_profile")}
                </button>
              ) : (
                <Link to="/trip-starter-form">
                  <button className="ProfileEditButton">
                    {t("edit_profile")}
                  </button>
                </Link>
              )}
              {/* "My referrals" lives in the menu ("Refer & earn"). The club
                  button shows in the app too: the dashboard opens inside the
                  profile tab's webview and "Back to Sotravel" returns here. */}
              <ClubProfileButton />
            </Stack>
          ) : (
            <Stack direction="row" spacing={1} style={{ marginTop: "12px", marginBottom: "8px" }}>
              <FollowButton profile={profile} />
              <button
                onClick={() => {
                  if (isInApp && window.ReactNativeWebView) {
                    window.ReactNativeWebView.postMessage(
                      JSON.stringify({
                        type: "navigateToDirectMessage",
                        userId: userId,
                      })
                    );
                  } else {
                    setShowDownloadAppModal(true);
                  }
                }}
                className="tw-mx-[5px] tw-mt-[1rem] tw-flex tw-h-[2rem] tw-flex-row tw-items-center tw-justify-center tw-rounded-[5px] tw-bg-[rgb(239,239,239)] tw-px-[18px] tw-py-[7px] tw-text-[14px] tw-font-bold tw-text-black"
                title={t("send_direct_message")}
              >
                <MessageCircle className="tw-mr-1.5 tw-h-4 tw-w-4" />
                {t("send_direct_message")}
              </button>
              {/* 3-dot menu button for desktop - placed beside Send Message button */}
              {!isUserProfile && !isMobileOnly && (
                <button
                  onClick={() => setShowActionSheet(true)}
                  className="tw-mx-[5px] tw-mt-[1rem] tw-flex tw-h-[2rem] tw-w-[2rem] tw-items-center tw-justify-center tw-rounded-[5px] tw-bg-[rgb(239,239,239)] tw-transition-colors hover:tw-bg-gray-200"
                  title="More options"
                >
                  <MoreHorizontal className="tw-h-5 tw-w-5 tw-text-gray-700" />
                </button>
              )}
            </Stack>
          )}

          <Stack className="ProfileSocialsContainer">
            {profile.socials_instagram && (
              <button
                className="ProfileSocialsButton"
                onClick={() => openInstagramProfile(profile.socials_instagram)}
              >
                <InstagramIcon
                  style={{
                    fontSize: "17px",
                    marginRight: "4px",
                    color: "deepskyblue",
                  }}
                />
                <span>Instagram</span>
              </button>
            )}
          </Stack>

          {is_trip_leader && (
            <div style={{ width: "100%" }}>
              <p className="ProfileHeader">
                {t("what_is_trip_leader")}
              </p>

              <div>
                <div className="ProfileDescription">
                  <span>
                    {t("trip_leader_description")}
                  </span>
                </div>
              </div>
            </div>
          )}

          {is_local_partner && local_partner_country ? (
            <p className="ProfileHeader" style={{ marginBottom: "0.5rem", marginTop: "0.5rem" }}>
              {t("word_from_team", { local_partner_country: local_partner_country })}
            </p>
          ) : (
            <p className="ProfileHeader" style={{ marginBottom: "0.5rem", marginTop: "0.5rem" }}>
              {t("about_person", { first_name: profile.display_name })}
            </p>
          )}

          <div className="ProfileDescription">{profile.description}</div>

          <Stack className="ProfileExtraInfoContainer" spacing={0.5}>
            {profile.host_type && (
              <div className="ProfileExtraInfoRow">
                <FlutterDashIcon style={{ fontSize: "18px" }} />{" "}
                <span
                  style={{ cursor: "pointer", textDecoration: "underline" }}
                  onClick={() => {
                    setOpenDuckLevelsDialog(true);
                  }}
                >
                  {t("duck_type", {
                    host_type: profile.influencer_type
                      ? profile.influencer_type
                      : profile.duck_badge === "samurai"
                        ? t("samurai.tier_name_badge", { defaultValue: "Super duck (Samurai)" })
                        : profile.host_type,
                  })}
                </span>
              </div>
            )}

            {profile.obsessed_with && (
              <div className="ProfileExtraInfoRow">
                <FavoriteBorderOutlinedIcon style={{ fontSize: "18px" }} />
                <span>
                  {t("obsessed_with")}: {profile.obsessed_with}
                </span>
              </div>
            )}

            {profile.certifications && (
              <div className="ProfileExtraInfoRow">
                <ArticleOutlinedIcon style={{ fontSize: "18px" }} />{" "}
                <span>
                  {t("certifications")}: {profile.certifications}
                </span>
              </div>
            )}

            {profile.school_and_subject && (
              <div className="ProfileExtraInfoRow">
                <SchoolOutlinedIcon style={{ fontSize: "18px" }} />{" "}
                <span>
                  {t("school")}: {profile.school_and_subject}
                </span>
              </div>
            )}

            {profile.work_and_role && (
              <div className="ProfileExtraInfoRow">
                <WorkOutlineOutlinedIcon style={{ fontSize: "18px" }} />{" "}
                <span>
                  {t("work")}: {profile.work_and_role}
                </span>
              </div>
            )}
          </Stack>

          {reviews.length > 0 && (
            <div className="TripDetailsArrowHeaderContainer">
              <span className="TripDetailsArrowHeader">
                {t("person_reviews", { first_name: profile.display_name })}
              </span>
              <Stack
                className="TripDetailslArrowContainer"
                flexDirection="row"
                justifyContent="space-between"
              >
                <button onClick={scrollReviewLeft}>
                  <ExpandCircleDownOutlinedIcon className="TripDetailsArrow left" />
                </button>

                <button onClick={scrollReviewRight}>
                  <ExpandCircleDownOutlinedIcon className="TripDetailsArrow right" />
                </button>
              </Stack>
            </div>
          )}
          {reviews.length > 0 && (
            <Stack className="TripDetailsCarouselItemContainer" ref={carouselReviewRef}>
              {reviews.map((review, index) => {
                return (
                  <div
                    className="TripDetailsCarouselItemBox"
                    style={
                      reviewMoreIndex === index
                        ? {
                            minWidth: review.positive.length * 2.1,
                          }
                        : {}
                    }
                  >
                    <span>
                      {reviewMoreIndex === index
                        ? `"${review.positive}"`
                        : shortenText(review.positive)}{" "}
                      {review.positive.length > reviewMaxLength && reviewMoreIndex !== index && (
                        <span
                          style={{ fontSize: "15px", fontWeight: "bold" }}
                          onClick={() => setReviewMoreIndex(index)}
                        >
                          {t("more")}
                        </span>
                      )}
                    </span>
                    <Stack
                      component="button"
                      type="button"
                      flexDirection="row"
                      alignItems="center"
                      className="TripDetailsReviewImgWrapper"
                      disabled={!review.review_by_user_id}
                      onClick={() => openReviewerProfile(review.review_by_user_id)}
                      aria-label={review.review_by_user_id ? t("filter_view_profile") : undefined}
                      style={{
                        appearance: "none",
                        background: "none",
                        border: 0,
                        padding: 0,
                        cursor: review.review_by_user_id ? "pointer" : "default",
                        textAlign: "left",
                      }}
                    >
                      <div className="TripDetailsReviewImgContainer">
                        <ImageResizable
                          size="s"
                          src={review.review_by_image}
                          alt={review.review_by_display_name || review.review_by_first_name}
                        />
                      </div>
                      <Stack flexDirection="column" alignItems="flex-start">
                        <span>{review.review_by_display_name || review.review_by_first_name}</span>
                        {review.review_by_host_type !== "Duckling" && (
                          <span style={{ fontSize: "12px", color: "#666", fontWeight: 500 }}>
                            {review.review_by_host_type}
                          </span>
                        )}
                      </Stack>
                    </Stack>
                  </div>
                );
              })}
            </Stack>
          )}

          {userUpcomingTrips.length > 0 && (
            <>
              <p className="ProfileHeader" style={{ marginTop: "1.5rem" }}>
                {t("upcoming_trips")}
              </p>
              <div className="ProfileDescription">
                {userUpcomingTrips.map((trip, index) => (
                  <TripCard trip={trip} index={index} profileUserId={userId} viewerUserId={user?.id} />
                ))}
              </div>
            </>
          )}

          {userPastTrips.length > 0 && (
            <>
              <p className="ProfileHeader">
                {t("previously_attended")}
              </p>
              <div className="ProfileDescription">
                {userPastTrips.map((trip, index) => (
                  <TripCard trip={trip} index={index} profileUserId={userId} viewerUserId={user?.id} />
                ))}
              </div>
            </>
          )}

          {extraMedia.length > 0 && (
            <div className="TripDetailsArrowHeaderContainer">
              <span className="TripDetailsArrowHeader">
                {t("person_in_action", { first_name: profile.display_name })}
              </span>
              <Stack
                className="TripDetailslArrowContainer"
                flexDirection="row"
                justifyContent="space-between"
              >
                <button onClick={scrollMediaLeft}>
                  <ExpandCircleDownOutlinedIcon className="TripDetailsArrow left" />
                </button>

                <button onClick={scrollMediaRight}>
                  <ExpandCircleDownOutlinedIcon className="TripDetailsArrow right" />
                </button>
              </Stack>
            </div>
          )}

          {extraMedia.length > 0 && (
            <Stack className="TripDetailsCarouselItemContainer" ref={carouselMediaRef}>
              {extraMedia
                .filter((media) => media.media_url)
                .map((media, index) => (
                  <div className="TripDetailsCarouselMediaBox">
                    <div className="TripDetailsCarouselVideoBox">
                      {displayExtraMedia(media.media_url, media.media_type)}
                    </div>
                  </div>
                ))}
            </Stack>
          )}

          <div style={{ width: "100%", height: "3rem" }}></div>
        </div>
      </div>

      <DuckLevelsDialog />

      <ReportUserDialog
        open={showReportDialog}
        onClose={() => setShowReportDialog(false)}
        userId={userId}
        userName={profile ? `${profile.display_name}` : ""}
        onSuccess={(message) => setOpenAlertBannerMsg(message)}
      />

      <DownloadAppModal
        open={showDownloadAppModal}
        handleClose={() => setShowDownloadAppModal(false)}
      />

      <ProfileActionSheetModal
        open={showActionSheet}
        onClose={() => setShowActionSheet(false)}
        userId={userId}
        userName={[profile?.first_name, profile?.last_name].filter(Boolean).join(" ") || profile?.username}
        onReportClick={() => setShowReportDialog(true)}
      />
    </div>
  );
};

export default UserProfile;

function TripCard({ trip, index, profileUserId, viewerUserId }) {
  // No target="_blank": this page renders inside the Expo app's webview, which silently drops new-window anchor clicks.
  return (
    <Link
      style={{ flex: "none", width: "140px" }}
      to={`/trips/${trip.project_id}`}
      onClick={(event) => {
        if (openOwnProfileBooking({ profileUserId, viewerUserId, tripId: trip.id })) {
          event.preventDefault();
          return;
        }
        tripLinkClick(`/trips/${trip.project_id}`, trip.project_id, trip)(event);
      }}
      key={trip.id}
    >
      {trip.main_images.length && (
        <ImageResizable
          src={trip.main_images[0]}
          alt={`trip-${trip.name}-${index}`}
          style={{
            width: "140px",
            height: "140px",
            objectFit: "cover",
            borderRadius: "10px",
            marginBottom: "0.5rem",
          }}
          size="l"
        />
      )}
      <div style={{ fontWeight: 600 }}>{trip.project_name}</div>
      <div>{getDDMMMDate(trip.start_date)}</div>
    </Link>
  );
}
